import { brandFeatures } from "@msb/reader-bridge";
import { useEffect, useState } from "preact/hooks";
import { App } from "../App.tsx";
import { AuthError, accessToken, loadTokens, startLogin } from "../auth/auth.ts";
import { config } from "../config.ts";
import { defineMessages, useT } from "../i18n/index.ts";
import type { OpenParams } from "../params.ts";
import { openReadingSession, SessionError } from "../shop/library.ts";

type State =
  | { kind: "loading" }
  | { kind: "ready"; params: OpenParams }
  // `message` : texte du serveur de publications, pour un code d'erreur qu'on ne connaît pas.
  | { kind: "error"; code: string; message?: string };

// Codes d'erreur du serveur de publications (server/library.go) que le lecteur peut rencontrer.
const messages = defineMessages(
  {
    "not-found": "Ce livre n'est pas dans votre bibliothèque.",
    "shop-unavailable": "La bibliothèque est momentanément indisponible.",
    drm: "Ce livre est protégé : lisez-le dans l'application MySmartBook.",
    format: "Ce format ne se lit pas encore sur le Web.",
    "no-file": "Aucun fichier disponible pour ce livre.",
    "download-failed": "Le fichier du livre n'a pas pu être récupéré.",
    error: "Impossible d'ouvrir ce livre.",
    opening: "Ouverture du livre…",
    back: "Retour à la bibliothèque",
  },
  {
    "not-found": "This book isn't in your library.",
    "shop-unavailable": "The library is temporarily unavailable.",
    drm: "This book is protected: read it in the MySmartBook app.",
    format: "This format can't be read on the web yet.",
    "no-file": "No file is available for this book.",
    "download-failed": "The book's file could not be retrieved.",
    error: "This book could not be opened.",
    opening: "Opening the book…",
    back: "Back to library",
  },
);

const sessionErrors = ["not-found", "shop-unavailable", "drm", "format", "no-file", "download-failed", "error"] as const;
const isKnownError = (code: string): code is (typeof sessionErrors)[number] => (sessionErrors as readonly string[]).includes(code);

export function ReadBook({ bookId, openChat = false }: { bookId: string; openChat?: boolean }) {
  const t = useT(messages);
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!loadTokens()) {
      void startLogin(`/lire/${encodeURIComponent(bookId)}`);
      return;
    }
    (async () => {
      try {
        const session = await openReadingSession(bookId);
        const token = await accessToken();
        setState({
          kind: "ready",
          params: {
            manifestUrl: session.manifestUrl,
            accessMode: session.accessMode,
            brand: config.brand,
            isSmartbook: session.isSmartbook,
            features: brandFeatures[config.brand],
            productId: session.productId,
            // Le chatbot accepte le jeton du compte, comme dans l'app mobile.
            chatToken: session.accessMode === "full" ? token : undefined,
            backUrl: `${window.location.origin}/`,
            libraryBookId: bookId,
            openChat,
          },
        });
      } catch (e) {
        if (e instanceof AuthError) {
          void startLogin(`/lire/${encodeURIComponent(bookId)}`);
          return;
        }
        setState(e instanceof SessionError ? { kind: "error", code: e.code, message: e.message } : { kind: "error", code: "error" });
      }
    })();
  }, [bookId]);

  if (state.kind === "ready") return <App params={state.params} />;
  return (
    <main class="page page--center">
      {state.kind === "loading" ? (
        <p class="reader-message">{t("opening")}</p>
      ) : (
        <div class="page-card" role="alert">
          <p>{isKnownError(state.code) ? t(state.code) : (state.message ?? t("error"))}</p>
          <a class="reader-button reader-button--primary" href="/">
            {t("back")}
          </a>
        </div>
      )}
    </main>
  );
}
